/**
 * Training needs — which operators run their machines harder than their site
 * peers do.
 *
 * For every operator with hours in the window: operator-sensitive events
 * (alarm codes with sensitivity `'high'` or `'partial'` whose component the
 * machine actually carries, plus attributed `shift_daily` abuse) per 100
 * operating hours, against the same rate pooled across every **other**
 * operator of the same primary class at the same site.
 *
 * A training need is flagged when all of these hold (defaults in
 * {@link TRAINING_DEFAULTS}):
 *
 * - ratio to peers ≥ 1.8;
 * - at least 20 operating hours and 5 events in the window;
 * - the excess is not chance: P(X ≥ events) for X ~ Poisson(peer rate ×
 *   hours / 100) is at most 0.05.
 *
 * The chance test matters because a driver with 30 hours and four events
 * against a clean peer group has a large ratio and almost no evidence.
 *
 * The peer pool deliberately includes other outliers: "the rest of the site"
 * is what a supervisor compares a driver against. Peers with no hours in the
 * window (crews off-swing) are ignored. The recommendation is chosen from the
 * operator's most frequent code.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over the
 * attribution. No React, no clock, no randomness.
 */

import type { MachineClass, MineCode } from '../../schema/machine.ts';
import type { TrainingNeed } from '../../schema/operations.ts';
import { ABUSE_CODE } from '../seed/alarm-codes.ts';
import { OPERATOR_BY_ID } from '../seed/operators.ts';
import { attribute, type Attribution } from './attribution.ts';
import { round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';

/** Thresholds for flagging a need. */
export interface TrainingOptions {
  /** Ratio to peers at or above which a need is flagged. Default 1.8. */
  readonly threshold?: number;
  /** Minimum operating hours in the window. Default 20. */
  readonly minHours?: number;
  /** Minimum events in the window. Default 5. */
  readonly minEvents?: number;
  /** Largest Poisson tail probability still flagged. Default 0.05. */
  readonly significance?: number;
}

export const TRAINING_DEFAULTS: Required<TrainingOptions> = {
  threshold: 1.8,
  minHours: 20,
  minEvents: 5,
  significance: 0.05,
};

/** P(X ≥ k) for X ~ Poisson(λ). */
export function poissonTail(k: number, lambda: number): number {
  if (k <= 0) return 1;
  if (lambda <= 0) return 0;
  let term = Math.exp(-lambda);
  let below = term; // P(X = 0)
  for (let i = 1; i < k; i += 1) {
    term *= lambda / i;
    below += term;
  }
  return Math.max(0, Math.min(1, 1 - below));
}

/** One operator's rate against their peers — flagged or not. */
export interface OperatorRate {
  operatorId: string;
  name: string;
  mine: MineCode;
  class: MachineClass;
  hours: number;
  events: number;
  ratePer100h: number;
  peerRate: number;
  /** `ratePer100h / peerRate`; 99 when peers are clean and the operator is not. */
  ratio: number;
  /** P(X ≥ events) at the peer rate over the operator's hours. */
  pValue: number;
  topCodes: { code: string; count: number }[];
  synthesised: boolean;
}

/** Every operator with hours in the window, highest ratio first. */
export function operatorRates(att: Attribution): OperatorRate[] {
  const hours = new Map<string, number>();
  for (const a of att.assignments) hours.set(a.operatorId, (hours.get(a.operatorId) ?? 0) + a.hours);
  const events = new Map<string, number>();
  const codes = new Map<string, Map<string, number>>();
  for (const e of att.events) {
    // Only codes that mean something on this machine: a sensitive code whose
    // component the machine does not carry (a brake code on a jumbo) is
    // source noise, not driving.
    if (e.sensitivity === 'none' || e.component === null) continue;
    events.set(e.operatorId, (events.get(e.operatorId) ?? 0) + e.count);
    const c = codes.get(e.operatorId) ?? new Map<string, number>();
    c.set(e.code, (c.get(e.code) ?? 0) + e.count);
    codes.set(e.operatorId, c);
  }

  // Pools per site × primary class.
  const pool = new Map<string, { hours: number; events: number }>();
  const poolKey = (id: string) => {
    const op = OPERATOR_BY_ID[id];
    return `${op.mine}|${op.classes[0]}`;
  };
  for (const [id, h] of hours) {
    const k = poolKey(id);
    const p = pool.get(k) ?? { hours: 0, events: 0 };
    p.hours += h;
    p.events += events.get(id) ?? 0;
    pool.set(k, p);
  }

  const out: OperatorRate[] = [];
  for (const [id, h] of hours) {
    if (h <= 0) continue;
    const op = OPERATOR_BY_ID[id];
    const ev = events.get(id) ?? 0;
    const p = pool.get(poolKey(id))!;
    const peerHours = p.hours - h;
    const peerEvents = p.events - ev;
    const rate = (ev / h) * 100;
    const peerRate = peerHours > 0 ? (peerEvents / peerHours) * 100 : 0;
    const ratio = peerRate > 0 ? rate / peerRate : rate > 0 ? 99 : 0;
    out.push({
      operatorId: id,
      name: op.name,
      mine: op.mine,
      class: op.classes[0],
      hours: round(h, 1),
      events: ev,
      ratePer100h: round(rate, 2),
      peerRate: round(peerRate, 2),
      ratio: round(ratio, 2),
      pValue: round(poissonTail(ev, (peerRate * h) / 100), 4),
      topCodes: [...(codes.get(id) ?? new Map<string, number>())]
        .map(([code, count]) => ({ code, count }))
        .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code))
        .slice(0, 3),
      synthesised: op.synthesised,
    });
  }
  return out.sort((a, b) => b.ratio - a.ratio || b.events - a.events);
}

/** What to recommend, keyed by the operator's most frequent code. */
export function recommendationFor(cls: MachineClass, topCode: string | undefined): string {
  switch (topCode) {
    case 'W-BRK-TEMP':
    case 'W-RET-DUTY':
    case 'E-BRK-TEMP-HIGH':
      return 'Decline descent refresher — retarder before service brake, 14 km/h on the 1:7 — with a trainer ride-along on the next night shift (SP-MT63-BRK-002 operator note).';
    case 'W-HYD-TEMP':
      return 'Loader duty-cycle coaching — no boom-overload holds, idle cool-down at crib — and a supervisor check of bucket technique at the face.';
    case 'W-SOC-LOW':
      return 'Battery-loader charge discipline — return to charge at 30 % state of charge, not on the warning — reviewed at pre-start.';
    case 'W-CHG-HANDSHAKE':
    case 'E-CHG-HANDSHAKE':
      return 'Wash-down procedure brief — cap the charge inlet, no high-pressure water on the connector.';
    case 'W-BREAKOUT-TORQUE':
    case 'E-ROD-DROP':
      return 'Rod-handling refresher — make-up torque and breakout sequence — with the drill trainer.';
    case 'W-DUST-HIGH':
      return 'Dust-suppression compliance — water on before collaring — reviewed by the drill & blast supervisor.';
    case 'W-ROT-TORQUE':
    case 'E-ROT-OVERSPEED':
    case 'E-PIPE-PARTED':
      return 'Feed-force and rotation technique coaching on the drill — back off feed at the first torque warning.';
    case 'W-TRN-SLIP':
    case 'W-TRN-OIL-TEMP':
      return 'Gear-selection coaching — no brake-throttle overlap, correct gear on the ramp.';
    case ABUSE_CODE:
    default:
      return cls === 'lhd'
        ? 'Bucket loading technique — no ramming the pile, no boom overload — with a trainer ride-along and a supervisor check after one week.'
        : cls === 'truck'
          ? 'Haul-truck driving refresher — no brake-throttle overlap, no harsh braking, correct gear on the decline.'
          : 'Drill operating technique refresher — collaring feed, flushing water on — with the drill trainer.';
  }
}

/**
 * Operators whose rate is at least `threshold` × their site peers', on enough
 * hours and events, and not explained by chance. Highest ratio first.
 */
export function trainingNeeds(att: Attribution, options: TrainingOptions = {}): TrainingNeed[] {
  const o = { ...TRAINING_DEFAULTS, ...options };
  return operatorRates(att)
    .filter(
      (r) =>
        r.ratio >= o.threshold && r.hours >= o.minHours && r.events >= o.minEvents && r.pValue <= o.significance,
    )
    .map((r) => {
      const op = OPERATOR_BY_ID[r.operatorId];
      return {
        operatorId: r.operatorId,
        name: r.name,
        mine: r.mine,
        crew: op.crew,
        class: r.class,
        hours: r.hours,
        ratePer100h: r.ratePer100h,
        peerRate: r.peerRate,
        ratio: r.ratio,
        pValue: r.pValue,
        events: r.events,
        topCodes: r.topCodes,
        recommendation: recommendationFor(r.class, r.topCodes[0]?.code),
        synthesised: r.synthesised,
      };
    });
}

/** Fetch, attribute and list training needs. */
export async function loadTrainingNeeds(options: TrainingOptions = {}): Promise<TrainingNeed[]> {
  const inputs = await loadReliabilityInputs();
  return trainingNeeds(attribute(inputs), options);
}
